import { createContext, useState, useEffect, useCallback } from "react";
import {
  setToLocalStorage,
  removeFromLocalStorage,
} from "../../lib/localStorage";
import { useLocation } from "react-router-dom";
import useCompositionLower from "config/useCompositionLower";

const TableContext = createContext();

export function TableContextProvider({ children }) {
  const location = useLocation();
  const { setLowerBound } = useCompositionLower();
  // Initial state setup from localStorage
  const initialTableStructures = [];
  const initialComplexTypes = [];
  const initialActiveComposition = JSON.parse(localStorage.getItem("activeComposition")) || "";
  const initialActiveComplexType = JSON.parse(localStorage.getItem("activeComplexType")) || "";
  const initialCelluarViewValue = JSON.parse(localStorage.getItem("hexagonView")) || false;
  const initialTableLimit = getLimit({ location, setLowerBound }) || 12;

  const initialTableConfig = JSON.parse(
    localStorage.getItem("tableConfig")
  ) || {
    itemsN: 0,
    currentValue: 0,
    paginationArr: JSON.parse(localStorage.getItem("paginationArr")) || [0],
    page: getPage({ location }) || 0
  };
  const initialDataForExport =
    JSON.parse(localStorage.getItem("initialDataForExport")) || [];
  const initialScopeConfig = JSON.parse(
    localStorage.getItem("scopeConfig")
  ) || { scope: "", scopes: [] };

  // State management with arrays
  const [hexagonView, setHexagonViewState] = useState(initialCelluarViewValue);
  const [compositionsObjectStructure, setCompositionsObjectStructure] = useState({});
  const [databaseStructureLoading, setDatabaseStructureLoading] = useState(false);
  const [tableStructure, setTableStructure] = useState(initialTableStructures);
  const [complexTypes, setComplexTypes] = useState(initialComplexTypes);
  const [activeComposition, setActiveComposition] = useState(initialActiveComposition);
  const [activeComplexType, setActiveComplexType] = useState(initialActiveComplexType);
  const [limit, setLimit] = useState(initialTableLimit);
  const [tableConfig, setTableConfig] = useState(initialTableConfig);
  const [scopeConfig, setScopeConfig] = useState(initialScopeConfig);
  const [selectedDataForExport, setSelectedDataForExport] = useState(initialDataForExport);

  useEffect(() => {
    setToLocalStorage("complexTypes", complexTypes);
  }, [complexTypes]);

  const handleSetActiveComposition = useCallback((active) => {
    if (!active) return;
    setToLocalStorage("activeComposition", active);
    setActiveComposition(active);
    setSelectedDataForExport([]);
    handleSetActiveComposition();
    setTableConfig({
      itemsN: 0,
      currentValue: 0,
      paginationArr: [0],
      page: 1
    });
    localStorage.setItem("paginationArr", JSON.stringify([0]));
  }, []);

  const setInitTabStructAndComplex = useCallback(
    (tableStructs, newComplexTypes) => {
      if (!Array.isArray(tableStructs) || !Array.isArray(newComplexTypes))
        return;
      setTableStructure(tableStructs);
      setComplexTypes(newComplexTypes);
    },
    []
  );

  const setDatabaseStructure = useCallback((newDatabaseStructure) => {
    // if (!Array.isArray(newDatabaseStructure)) return;
    if (!newDatabaseStructure) return;
    setTableStructure(newDatabaseStructure?.tables);
    setComplexTypes(newDatabaseStructure?.structures);
    setScopeConfig((prev) => ({ ...prev, scopes: newDatabaseStructure?.scopes }));
  }, []);

  const updateTableStructure = useCallback((newTableStructure) => {
    if (!Array.isArray(newTableStructure)) return;
    setTableStructure((prev) => {
      const updatedStructure = Array.from(
        new Set([...prev, ...newTableStructure])
      ); // Combine and remove duplicates
      return updatedStructure;
    });
  }, []);

  const updateComplexTypes = useCallback((newComplexTypes) => {
    if (!Array.isArray(newComplexTypes)) return;
    setComplexTypes((prev) => {
      const updatedTypes = Array.from(new Set([...prev, ...newComplexTypes]));
      return updatedTypes;
    });
  }, []);

  // Additional helper functions remain unchanged

  const valueToShare = {
    setCompositionsObjectStructure,
    compositionsObjectStructure,
    retriveComplexTypes: () => complexTypes,
    tableStructures: tableStructure,
    updateComplexTypes,
    setDatabaseStructure,
    setDatabaseStructureLoading,
    databaseStructureLoading,
    updateTableStructureContextData: updateTableStructure,
    emptyTable: () => {
      const dataOnLSToBeCleared = [
        "scopeConfig",
        "complexTypes",
        "tableStructure",
        "activeComposition",
        "activeComplexType",
        "tableConfig",
        "initialDataForExport",
      ];
      dataOnLSToBeCleared.forEach(removeFromLocalStorage);
      setTableStructure([]);
      setComplexTypes([]);
      setTableConfig(initialTableConfig);
      setScopeConfig(initialScopeConfig);
      setActiveComposition("");
      setActiveComplexType("");
    },
    activeComposition,
    setActiveComposition: handleSetActiveComposition,
    setInitTabStructAndComplex,
    activeComplexType,
    setActiveComplexType,
    hexagonView,
    setHexagonView: () => setHexagonViewState((prev) => !prev),
    tableConfig,
    limit,
    scopeConfig,
    setScopeConfig,
    selectedDataForExport,
    setSelectedDataForExport,
    setLimit,
    setTableConfig,
    returnActiveTableStructure: () =>
      tableStructure.find(
        (structure) => structure.name === activeComposition
      ) || {},
    getComplexStructure: (nameOfStruct) => complexTypes.find((complex) => complex.name === nameOfStruct) || [],
  };

  return (
    <TableContext.Provider value={valueToShare}>
      {children}
    </TableContext.Provider>
  );
}

export default TableContext;

const getLimit = ({ location, setLowerBound }) => {
  const lsLimit = localStorage.getItem("paginationLimit");
  const params = new URLSearchParams(location.search);

  // Get the limit from URL, default to 12 if not present or invalid
  let limit = params.get("limit");
  limit = isNaN(Number(limit)) ? 12 : Number(limit);

  // If there is a stored limit in localStorage
  if (lsLimit) {
    // If the stored limit is the same as the one from URL, return it but ensure it doesn't exceed 100
    if (Number(lsLimit) === limit) {
      return Math.min(limit, 100);
    } else {
      // If the URL limit is different from the stored limit, update localStorage with the new limit
      localStorage.setItem("paginationLimit", limit);
      localStorage.setItem("paginationArr", JSON.stringify([0]));
      // setLowerBound(0);
      return Math.min(limit, 100);
    }
  } else {
    // If no stored limit, set localStorage to the URL limit and return the value
    localStorage.setItem("paginationLimit", limit);
    return Math.min(limit, 100);
  }
};

const getPage = ({ location }) => {
  const params = new URLSearchParams(location.search);
  let page = params.get("page");
  page = isNaN(Number(page)) || Number(page) <= 0 ? 1 : Number(page);
  return page;
}